import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import Loader from '@/components/Loader';
import { getAttemptImagesById, useGetAttemptsByWorkOrderId, useSendAttemptMail } from '@/services/attemptService';
import type { GetAttemptImagesResponseById } from '@/types/workOrderAttempt.types';
import { ArrowLeft, ArrowRight, CircleCheck } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import AlertBox from '@/components/AlertBox';
import { displayError } from '@/lib/error';

const getAttemptCommentText = (comment?: string | null): string => comment?.trim() ?? '';

export const ShareAttemptImageDialog = ({
  onCloseClick,
  attemptIds,
  workOrderId,
  recipientEmail,
}: {
  onCloseClick: () => void;
  attemptIds: string[];
  workOrderId: string;
  recipientEmail: string;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [attemptData, setAttemptData] = useState<GetAttemptImagesResponseById | null>(null);
  const [selectedImages, setSelectedImages] = useState<Record<string, string[]>>({});

  const currentAttemptId = attemptIds?.[currentIndex] || '';
  const sentAttemptMail = useSendAttemptMail();
  const [displayConfirmationDialog, setDisplayConfirmationDialog] = useState(false);
  const [includeAttemptComments, setIncludeAttemptComments] = useState(false);
  const { data, isFetching } = getAttemptImagesById(currentAttemptId, workOrderId);
  const { data: attemptList } = useGetAttemptsByWorkOrderId(workOrderId);

  const commentItems = (attemptList?.homeInspectionAttempts ?? []).map((hiAttempt) => ({
    attemptId: hiAttempt.attemptId,
    text: getAttemptCommentText(hiAttempt.comments),
  }));
  const hasAnyComment = commentItems.some((item) => item.text.length > 0);

  useEffect(() => {
    if (data) {
      setAttemptData(data);
      setSelectedImages((prev) => ({ ...prev, [currentAttemptId]: prev[currentAttemptId] || [] }));
    }
  }, [data, currentAttemptId]);

  const currentSelectedImages = selectedImages[currentAttemptId] || [];

  const toggleImageSelect = (imageId: string) => {
    setSelectedImages((prev) => {
      const prevSelected = prev[currentAttemptId] || [];
      const updated = prevSelected.includes(imageId)
        ? prevSelected.filter((id) => id !== imageId)
        : [...prevSelected, imageId];
      return { ...prev, [currentAttemptId]: updated };
    });
  };

  const handleSelectAll = () => {
    if (!attemptData?.images?.length) return;
    setSelectedImages((prev) => ({
      ...prev,
      [currentAttemptId]:
        currentSelectedImages.length === attemptData.images.length ? [] : attemptData.images.map((img) => img.mediaId),
    }));
  };

  const handleSendEmail = () => {
    sentAttemptMail.mutate(
      { workOrderId, imageIds: Object.values(selectedImages).flat(), includeAttemptComments: hasAnyComment && includeAttemptComments },
      {
        onSuccess: (data) => {
          toast({
            description: data.message,
          });
          setDisplayConfirmationDialog(false);
          onCloseClick();
        },
        onError: (error) => {
          displayError(error, 5000);
          setDisplayConfirmationDialog(false);
          onCloseClick();
        },
      }
    );
  };

  return (
    <Dialog open={true} onOpenChange={onCloseClick}>
      <DialogContent className="max-w-[50vw] h-[80vh] bg-white flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex justify-between items-center">
            <div className="flex w-full flex-col gap-3">
              <h2 className="text-lg font-semibold mb-1">Select Images</h2>
              <p className="text-sm text-gray-500">Select the images to include in the attempt’s update email</p>
              <div className="flex flex-row justify-between">
                <p className="text-sm font-medium mb-2 text-primary">Attempt {currentIndex + 1} Images:</p>
                {attemptData?.images?.length && (
                  <span
                    onClick={handleSelectAll}
                    className="text-sm text-primary font-medium hover:underline border-0 hover:bg-none cursor-pointer"
                  >
                    {attemptData?.images?.length && currentSelectedImages.length === attemptData.images.length
                      ? 'Unselect All'
                      : 'Select All'}
                  </span>
                )}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <Loader isLoading={isFetching}>
            {!attemptData?.images?.length ? (
              <div className="text-center py-8 text-gray-500">No images found for this attempt.</div>
            ) : (
              <div className="flex flex-col h-full">
                <div
                  className={`grid gap-4 pr-8 pb-[20px] custom-scrollbar`}
                  style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}
                >
                  {attemptData.images.map((img) => {
                    const imageSrc = img.imageBuffer;
                    const isSelected = currentSelectedImages.includes(img.mediaId);

                    return (
                      <div key={img.mediaId} className="w-full">
                        <div
                          onClick={() => toggleImageSelect(img.mediaId)}
                          className="relative w-fit rounded-lg border cursor-pointer transition-all "
                        >
                          <div className="relative flex ">
                            <img src={imageSrc} alt="attempt" className="object-cover flex flex-1 h-32 rounded-md " />
                            {isSelected && (
                              <div className="absolute -top-3 -right-3 z-50 bg-white rounded-full shadow-md p-[2px]">
                                <CircleCheck fill="#1B8652" color="#fff" size={20} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Loader>
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-3 flex !flex-col gap-2">
          <div className="flex w-full mt-4 justify-between items-center">
            {currentIndex !== 0 && (
              <div
                className="gap-2 flex items-center text-sm cursor-pointer text-primary"
                onClick={() => setCurrentIndex((i) => i - 1)}
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Previous</span>
              </div>
            )}

            <div className="flex-1" />
            {currentIndex !== attemptIds.length - 1 && (
              <div
                className="gap-2 flex items-center text-sm cursor-pointer text-primary ml-auto"
                onClick={() => setCurrentIndex((i) => i + 1)}
              >
                <span>Next</span>
                <ArrowRight className="h-4 w-4" />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1 w-full mt-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="include-attempt-comments"
                checked={hasAnyComment && includeAttemptComments}
                disabled={!hasAnyComment}
                onCheckedChange={(checked) => {
                  if (!hasAnyComment) return;
                  setIncludeAttemptComments(checked === true);
                }}
              />
              <Label
                htmlFor="include-attempt-comments"
                className={`font-normal text-sm ${hasAnyComment ? 'cursor-pointer' : 'cursor-not-allowed text-[#71717A]'}`}
              >
                Include comments
              </Label>
            </div>
            <div className="pl-6 text-sm break-words max-h-24 overflow-y-auto text-[#71717A]">
              {hasAnyComment ? (
                <span className="flex flex-col gap-1">
                  {commentItems.map((item, index) => (
                    <span key={item.attemptId} className={item.text ? 'text-black' : 'text-[#71717A]'}>
                      {commentItems.length > 1 ? `Attempt ${index + 1}: ` : ''}
                      {item.text || 'No comments'}
                    </span>
                  ))}
                </span>
              ) : (
                'No comments'
              )}
            </div>
          </div>

          <div className="flex flex-row gap-4 justify-end w-full mt-2">
            <Button variant="outline" className="h-8" onClick={onCloseClick}>
              Cancel
            </Button>
            <Button
              className="h-8"
              onClick={() => {
                setDisplayConfirmationDialog(true);
              }}
              isLoading={sentAttemptMail.isPending}
            >
              Email Attempt Updates
            </Button>
          </div>
          {displayConfirmationDialog && (
            <AlertBox
              title="Are you sure?"
              description={`Are you sure you want to share the status update with ${recipientEmail}?`}
              open={displayConfirmationDialog}
              cancelBtnLabel="No"
              confirmBtnLabel="Yes"
              isLoading={sentAttemptMail.isPending}
              cancelBtnClick={() => {
                setDisplayConfirmationDialog(false);
              }}
              confirmBtnClick={() => {
                handleSendEmail();
              }}
            />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
